import React, { useEffect, useMemo, useState } from 'react';
import PageContainer from '../../layouts/PageContainer';
import PageHeader from '../../components/Nav/PageHeader';
import { useConfig } from '../../DataStore/ConfigContext';
import Notification from '../../components/Common/Notification';
import ActivityLoader from '../../components/Common/ActivityLoader';
import functions from '../../util/functions';
import CardComponent from '../../components/Common/CardComponent';
import { Link } from 'react-router-dom';
import InviteUserModal from '../../components/Config/InviteUserModal';
import AlertDeleteUser from '../../components/Config/AlertDeleteUser';
import ChangeUserRoleModal from '../../components/Config/ChangeUserRoleModal';

const UserMgtPage = () => {
  const [activeRecord, setActiveRecord] = useState(null);
  const { getUsers, userListing, getUserRoles, userRoles } = useConfig();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);

  useEffect(() => {
    getUsers();
    getUserRoles();
  }, []);

  const handleNewUser = () => setShowInviteModal(true);

  const handleDelete = () => {
    if (!activeRecord) return; // safety guard
    setShowDeleteModal(true);
  };

  const handleChangeRole = () => {
    if (!activeRecord) return; // safety guard
    setShowChangeRoleModal(true);
  }

  const handleRowClick = (user) => {
    setActiveRecord((prev) => (prev?.user_id === user.user_id ? null : user));
  };

  const headerDropdown = useMemo(
    () => ({
      label: 'Action',
      icon: 'fas fa-cog',
      buttonClass: 'btn-outline-secondary',
      items: [
        { label: 'Invite User', icon: 'fa fa-paper-plane', onClick: handleNewUser },
        { label: 'Refresh', icon: 'fas fa-sync', onClick: () => getUsers(true) },
        {
          label: 'Remove User',
          icon: 'fas fa-trash',
          onClick: handleDelete,
          disabled: !activeRecord, // will render disabled in PageHeader (if supported)
          title: !activeRecord ? 'Select a user to remove' : `Remove ${activeRecord?.full_name}`,
        },
        {
          label: 'Change User Role',
          icon: 'fas fa-edit',
          onClick: handleChangeRole,
          disabled: !activeRecord, // will render disabled in PageHeader (if supported)
          title: !activeRecord ? 'Select a user to change role' : `Change role for ${activeRecord?.full_name}`,
        },
      ],
    }),
    [activeRecord]
  );

  return (
    <>
      <PageContainer>
        <PageHeader
          title="User Management"
          breadcrumb={
            <>
              <li className="breadcrumb-item">
                <Link to="/settings">Settings</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                User Management
              </li>
            </>
          }
          // If your PageHeader supports a `right` prop or similar, you can also show the selection status up top
          extraRight={
            activeRecord ? (
              <span className="badge badge-primary">
                Selected: {activeRecord.full_name}
              </span>
            ) : null
          }
          dropdown={headerDropdown}
        />

        <CardComponent
          title="User system listing"
          subtitle="Manage and invite users to access your system"
        >
          <div className="table-responsive">
            <table className="align-middle mb-3 table table-borderless table-striped table-hover">
              <thead>
                <tr>
                  <th className="text-center">&nbsp;</th>
                  <th>Full Name</th>
                  <th>Role</th>
                  <th className="text-left">Username</th>
                  <th className="text-center">Phone Number</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(userListing) &&
                  userListing.map((user, index) => {
                    const isActive = user.user_id === activeRecord?.user_id;
                    return (
                      <tr
                        key={user.user_id}
                        onClick={() => handleRowClick(user)}
                        className={isActive ? 'row-active' : ''}
                        role="button"
                        aria-selected={isActive}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(user);
                          }
                        }}
                        title={isActive ? 'Selected' : 'Click to select'}
                      >
                        <td className="text-center text-muted">#{index + 1}</td>
                        <td className="text-left">{user.full_name}</td>
                        <td className="text-left">{user?.role?.role_name}</td>
                        <td className="text-left">{user.username}</td>
                        <td className="text-center">{user.phone_number}</td>
                        <td className="text-center">
                          <div
                            className={`badge badge-${
                              user.status === 'Active' ? 'success' : 'warning'
                            }`}
                          >
                            {user.status}
                          </div>
                        </td>
                        <td className="text-center">
                          {functions.formatDate(user.updated_at)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>

            {userListing === '404' && (
              <Notification message="No system user found" type="info" />
            )}
            {!userListing && <ActivityLoader />}
          </div>
        </CardComponent>
      </PageContainer>

      <InviteUserModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        roles={userRoles}
        onSuccess={() => {
          setShowInviteModal(false);
          getUsers(true);
        }}
      />

      <AlertDeleteUser
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        deletedUser={activeRecord}
        onSuccess={() => {
              setShowDeleteModal(false);
              getUsers(true);
      }}
      />

      <ChangeUserRoleModal
        show={showChangeRoleModal}
        onClose={() => setShowChangeRoleModal(false)}
        roles={userRoles}
        user={activeRecord}
        onSuccess={() => {
          setShowChangeRoleModal(false);
          getUsers(true);
        }}
      />
    </>
  );
};

export default UserMgtPage;
